/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { useTheme } from 'emotion-theming';
import { Theme } from 'src/design-system/theme';
import { FontColors } from 'src/design-system/style-types';
import { Box } from '../Box';
import { Icon } from '../Icon/Icon';
import { IconName } from '../Icon/Icon.types';

interface Props {
  icons: IconName[];
  // border colour of the icon circles; defaults to the standard grey
  borderColor?: 'grey' | 'green';
  // colour of the icon glyphs; pass a design-system grey when using the
  // monochrome icon variants (e.g. 'php2 grey') for an inactive state
  iconColor?: FontColors;
}

export const IconStack = ({ icons, borderColor = 'grey', iconColor }: Props) => {
  const theme = useTheme<Theme>();
  const borderColorCss =
    borderColor === 'green'
      ? theme.colors.greenBright
      : theme.settings.darkMode
      ? theme.colors.lightAlways
      : theme.colors.grey10;
  // chip background inverts to the dark surface in dark mode; the green ring
  // marks the active state and stays green in both modes
  const chipBackground = theme.settings.darkMode ? theme.colors.dark : 'white';
  // fontColors.dark already resolves to white in dark mode (see ColorBuilder),
  // so only an explicitly-requested grey needs inverting to white
  const glyphColor = theme.settings.darkMode && iconColor === 'grey' ? 'light' : iconColor;
  const iconCss = css`
    width: 32px;
    height: 32px;
    box-sizing: border-box;

    display: flex;
    align-items: center;
    justify-content: center;

    padding: 2px;
    border: 1px solid ${borderColorCss};
    border-radius: 50%;

    &:not(:first-child) {
      margin-left: -8px;
    }

    background-color: ${chipBackground};
    // monochrome icons (e.g. 'php2 grey') use this as the knockout colour for
    // any lettering so it stays readable against the chip in both modes
    --iconstack-inverse: ${chipBackground};
  `;

  return (
    <Box w="auto" j="center">
      {icons.map((icon, index) => (
        <Box key={icon} css={iconCss}>
          <Icon name={icon} size="lg" color={glyphColor} />
        </Box>
      ))}
    </Box>
  );
};
